import { useCallback, useEffect, useRef, useState } from 'react';
import { tacticalApi } from '../../lib/api';
import { toast } from 'sonner';

const AUTOSAVE_DELAY_MS = 1500; // wait 1.5s of inactivity before autosaving

/**
 * Encapsulates tactical autosave + manual-save behaviour:
 *  - debounced silent save when `formData` changes (owner only)
 *  - beforeunload guard if local edits are unsaved
 *  - flush-on-unmount best-effort
 *  - manual save with toast (Save button)
 *
 * Returns: { saveStatus, lastSavedAt, handleSaveWithStatus, saving }
 */
export function useTacticalAutosave({ tactical, formData, onUpdate, isOwner }) {
  const [saving, setSaving] = useState(false);
  // Status: 'idle' (no changes yet), 'unsaved', 'saving', 'saved', 'error'
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autosaveTimerRef = useRef(null);
  const lastSavedSnapshotRef = useRef(JSON.stringify(formData));
  const isMountedRef = useRef(true);
  const skipNextAutosaveRef = useRef(true); // initial mount shouldn't trigger autosave

  // Keep the LATEST formData and tactical.id in refs so the flush-on-unmount
  // cleanup can read the live values instead of the stale first-render closure
  // (this is the bug that wiped TAH data when a user navigated away mid-debounce).
  const formDataRef = useRef(formData);
  const tacticalIdRef = useRef(tactical?.id);
  const errorNotifiedRef = useRef(false);
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { tacticalIdRef.current = tactical?.id; }, [tactical?.id]);

  // Silent save used by autosave — no toast spam
  const silentSave = useCallback(async () => {
    if (!tactical?.id) return;
    setSaveStatus('saving');
    try {
      const snapshot = JSON.stringify(formData);
      const updated = await tacticalApi.update(tactical.id, formData);
      if (!isMountedRef.current) return;
      lastSavedSnapshotRef.current = snapshot;
      setLastSavedAt(new Date());
      setSaveStatus('saved');
      errorNotifiedRef.current = false;
      // Update parent silently so derived state stays correct, but don't blow away local edits
      if (typeof onUpdate === 'function') {
        onUpdate(updated, { silent: true });
      }
    } catch (err) {
      console.error('Autosave failed:', err);
      if (isMountedRef.current) {
        setSaveStatus('error');
        // A long editing session can outlive the 24h session token. Failing
        // quietly here means the user keeps typing into a form that is no
        // longer being saved, so say it out loud — once per failure streak.
        if (!errorNotifiedRef.current) {
          errorNotifiedRef.current = true;
          const expired = err?.response?.status === 401 || err?.response?.status === 403;
          toast.error(
            expired
              ? 'Your session expired — your changes are NOT saved. Open this page in a new tab, sign in, then come back and press Save.'
              : 'Could not save your changes. Keep this tab open and press Save to retry.',
            { duration: Infinity, id: 'autosave-error' },
          );
        }
      }
    }
  }, [tactical?.id, formData, onUpdate]);

  // Watch formData → schedule debounced autosave
  useEffect(() => {
    if (!isOwner) return; // managers use add-item paths separately
    if (!tactical?.id) return;

    // Skip the initial render
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      lastSavedSnapshotRef.current = JSON.stringify(formData);
      return;
    }

    const currentSnapshot = JSON.stringify(formData);
    if (currentSnapshot === lastSavedSnapshotRef.current) return;

    setSaveStatus('unsaved');

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      silentSave();
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [formData, isOwner, tactical?.id, silentSave]);

  // Warn before unload if dirty
  useEffect(() => {
    const beforeUnload = (e) => {
      if (JSON.stringify(formData) !== lastSavedSnapshotRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [formData]);

  // Track mount state for async-safe state setters; flush pending autosave on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        // Best-effort flush using REFS so we send the latest formData, not the
        // stale closure from first render. Without this, navigating away mid-
        // debounce wiped newly-enabled TAH sites because we PUT the old shape.
        const latestId = tacticalIdRef.current;
        const latestPayload = formDataRef.current;
        if (latestId && JSON.stringify(latestPayload) !== lastSavedSnapshotRef.current) {
          tacticalApi.update(latestId, latestPayload).catch(() => {});
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual save (Save button) — toasts + updates autosave snapshot
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const updated = await tacticalApi.update(tactical.id, formData);
      onUpdate(updated);
      toast.success('Tactical saved successfully');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save tactical');
    } finally {
      setSaving(false);
    }
  }, [tactical?.id, formData, onUpdate]);

  const handleSaveWithStatus = useCallback(async () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    await handleSave();
    if (isMountedRef.current) {
      lastSavedSnapshotRef.current = JSON.stringify(formData);
      setLastSavedAt(new Date());
      setSaveStatus('saved');
    }
  }, [handleSave, formData]);

  return { saveStatus, lastSavedAt, handleSaveWithStatus, saving };
}
